import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { getDoctorStats, getHospitalStats, DoctorStats, HospitalStats } from '../api/api';

interface StatsPageProps {
  type: 'doctors' | 'hospitals';
}

// 'all' sends no window to the API, which then aggregates over all time.
type Period = number | 'all';

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: 3, label: 'Last 3 months' },
  { value: 6, label: 'Last 6 months' },
  { value: 12, label: 'Last year' },
  { value: 24, label: 'Last 2 years' },
  { value: 'all', label: 'All time' },
];

const StatsPage: React.FC<StatsPageProps> = ({ type }) => {
  const [doctorStats, setDoctorStats] = useState<DoctorStats[]>([]);
  const [hospitalStats, setHospitalStats] = useState<HospitalStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>(12);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const months = period === 'all' ? undefined : period;
      try {
        if (type === 'doctors') {
          const data = await getDoctorStats(500, months);
          setDoctorStats(data);
        } else {
          const data = await getHospitalStats(500, months);
          setHospitalStats(data);
        }
      } catch (error) {
        console.error(`Error fetching ${type} stats:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [type, period]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const periodLabel =
    PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? '';

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4">
            {type === 'doctors' ? 'Doctor Statistics' : 'Hospital Statistics'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {periodLabel}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="stats-period-label">Period</InputLabel>
          <Select
            labelId="stats-period-label"
            label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
      {type === 'doctors' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Doctors
                </Typography>
                <Typography variant="h4">
                  {doctorStats.length}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Appointments
                </Typography>
                <Typography variant="h4">
                  {doctorStats.reduce((sum, doc) => sum + doc.appointment_count, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(doctorStats.reduce((sum, doc) => sum + doc.total_amount, 0))}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      )}

      {type === 'hospitals' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Hospitals
                </Typography>
                <Typography variant="h4">
                  {hospitalStats.length}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Appointments
                </Typography>
                <Typography variant="h4">
                  {hospitalStats.reduce((sum, hosp) => sum + hosp.appointment_count, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(hospitalStats.reduce((sum, hosp) => sum + hosp.total_amount, 0))}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{type === 'doctors' ? 'Doctor Name' : 'Hospital Name'}</TableCell>
                <TableCell align="right">Appointments</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="right">
                  {type === 'doctors' ? 'Unique Patients' : 'Unique Doctors'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {type === 'doctors' &&
                doctorStats.map((doctor, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      {doctor.doctor_name}
                    </TableCell>
                    <TableCell align="right">{doctor.appointment_count}</TableCell>
                    <TableCell align="right">{formatCurrency(doctor.total_amount)}</TableCell>
                    <TableCell align="right">{doctor.patient_count}</TableCell>
                  </TableRow>
                ))}
              {type === 'hospitals' &&
                hospitalStats.map((hospital, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      {hospital.hospital_name}
                    </TableCell>
                    <TableCell align="right">{hospital.appointment_count}</TableCell>
                    <TableCell align="right">{formatCurrency(hospital.total_amount)}</TableCell>
                    <TableCell align="right">{hospital.doctor_count}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </>
      )}
    </Box>
  );
};

export default StatsPage;
